import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Rectangle,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceLine,
  Line,
} from "recharts";
import { fmtCurrency, fmtCurrencyK } from "../lib/formatters";
import { ChevronLeft } from "lucide-react";
import { DelayedTooltipContent } from "../lib/DelayedTooltip";
import { useSettings } from "../contexts/SettingsContext";
import FundingLevelAnalysisChart from "./FundingLevelAnalysisChart";

const ADJ_GREEN = "#2E9E5B";
const RESERVE_GREEN = "#7BC81E";

// Custom two-column tooltip for the Reserve Activity chart (Dave 2026-08-05):
// label on the left, value right-aligned in a tabular-nums monospace column so
// year-over-year comparisons scan cleanly. Defined at module scope so React
// doesn't reconstruct the component on every render.
function ActivityTooltip({ active, payload, label, revealedLabel }) {
  if (!active || !payload || payload.length === 0) return null;
  // 2026-02-27 (evening, Dave) — Click-to-reveal gate. The Reserve
  // Activity chart used to open its detail tooltip on hover (with a
  // small delay). To match the app-wide UI pattern in
  // ElementsCostBars / Waterfall — hover shows only a light-gray
  // column tint, click reveals the details — we short-circuit the
  // tooltip content unless the currently-hovered column is the one
  // the user has explicitly clicked (`revealedLabel === label`).
  // The chart's `cursor` fill still renders on hover to signal the
  // column is clickable.
  // Hover delay (DelayedTooltipContent) or a click (forceLabel) reveals details.

  // 2026-02-23 (Dave) — Expenditures group now renders as:
  //   Expenditures          $X            (NOT charted; header only)
  //     Reserves            $X - $SA      (charted light blue)
  //     Special Assessments $SA           (charted coral)
  //       Per Member        $SA/units     (indented sub-sub-row)
  // Positive rows above the group (Contributions, Adjustment) render
  // as before.
  const src = payload.find((p) => p && p.payload)?.payload || {};
  const perMemberSA = src.saPerMember ?? null;
  const perMemberContrib = src.contribPMPM ?? null;

  // Top rows = the positive half of the stack (Contributions,
  // Contribution Adjustment). We handle Reserves + Special Assessments
  // ourselves under a shared "Expenditures" header so recharts' auto
  // iteration doesn't duplicate them.
  const topRows = payload.filter(
    (p) =>
      p &&
      (p.dataKey === "contribution" || p.dataKey === "adjustment" || p.dataKey === "adjustmentExcess") &&
      p.value !== 0 &&
      p.value != null,
  );

  const expTotal = src.expTotal || 0;
  const reservesFunded = src.reservesFunded || 0;
  const saAmount = src.saAmount || 0;
  const hasExpGroup = expTotal > 0 || saAmount > 0;

  const reserveBal = src.reserveBalance;
  if (topRows.length === 0 && !hasExpGroup && reserveBal == null) return null;

  const rowBase = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 16,
  };

  return (
    <div
      className="bg-white text-[11px]"
      style={{
        background: "#FFFFFF",
        border: "1px solid #1C4E80",
        borderRadius: 3,
        padding: "3px 9px 4px",
        minWidth: 220,
        boxShadow: "0 4px 14px rgba(15, 42, 68, 0.12)",
        pointerEvents: "none",
        lineHeight: 1.25,
      }}
      data-testid="activity-tooltip"
    >
      <div style={{ fontWeight: 700, color: "#1C4E80", marginBottom: 1 }}>
        Year {label}
      </div>
      {topRows.map((p) => (
        <React.Fragment key={p.dataKey}>
          <div style={{ ...rowBase, color: "#1A1A1A" }}>
            <span>{p.name}</span>
            <span className="font-mono tabular-nums">
              {fmtCurrency(Math.abs(src.raw?.[p.dataKey] ?? p.value))}
            </span>
          </div>
          {p.dataKey === "contribution" && perMemberContrib != null && (
            <div
              style={{ ...rowBase, color: "#1A1A1A", paddingLeft: 18 }}
              data-testid="activity-tooltip-contrib-pmpm"
            >
              <span>Per Member / Month</span>
              <span className="font-mono tabular-nums">
                {fmtCurrency(perMemberContrib)}
              </span>
            </div>
          )}
        </React.Fragment>
      ))}
      {hasExpGroup && (
        <>
          {/* Header — full Expenditures amount. NOT charted. Dark ink
              so it reads as the parent of the two indented children. */}
          <div
            style={{ ...rowBase, color: "#1A1A1A" }}
            data-testid="activity-tooltip-expenditures"
          >
            <span>Expenditures</span>
            <span className="font-mono tabular-nums">
              {fmtCurrency(-(expTotal || saAmount))}
            </span>
          </div>
          {reservesFunded > 0 && (
            <div
              style={{ ...rowBase, color: "#1A1A1A", paddingLeft: 18 }}
              data-testid="activity-tooltip-reserves"
            >
              <span>From Reserves</span>
              <span className="font-mono tabular-nums">
                {fmtCurrency(-reservesFunded)}
              </span>
            </div>
          )}
          {saAmount > 0 && (
            <>
              <div
                style={{ ...rowBase, color: "#1A1A1A", paddingLeft: 18 }}
                data-testid="activity-tooltip-special"
              >
                <span>Special Assessments</span>
                <span className="font-mono tabular-nums">
                  {fmtCurrency(-saAmount)}
                </span>
              </div>
              {perMemberSA != null && (
                <div
                  style={{ ...rowBase, color: "#1A1A1A", paddingLeft: 36 }}
                  data-testid="activity-tooltip-sa-per-member"
                >
                  <span>Per Member</span>
                  <span className="font-mono tabular-nums">
                    {fmtCurrency(perMemberSA)}
                  </span>
                </div>
              )}
            </>
          )}
        </>
      )}
      {reserveBal != null && (
        <div
          style={{ ...rowBase, color: "#000000", fontWeight: 600, borderTop: "0.5px solid #E2E8F0", marginTop: 2, paddingTop: 2 }}
          data-testid="activity-tooltip-reserve-balance"
        >
          <span>Reserve Balance</span>
          <span className="font-mono tabular-nums">{fmtCurrency(reserveBal)}</span>
        </div>
      )}
      {src.fullyFunded > 0 && (
        <div style={{ ...rowBase, color: "#1A1A1A" }} data-testid="activity-tooltip-fully-funded">
          <span>Fully Funded Balance</span>
          <span className="font-mono tabular-nums">{fmtCurrency(src.fullyFunded)}</span>
        </div>
      )}
      {src.fundingPct != null && (
        <div style={{ ...rowBase, color: "#1A1A1A" }} data-testid="activity-tooltip-funding-level">
          <span>Funding Level</span>
          <span className="font-mono tabular-nums">{src.fundingPct}%</span>
        </div>
      )}
    </div>
  );
}

// Reserve Activity by Year (full-width bottom chart on the Dashboard).
// Adds the "Contribution Adjustment" (gray) stack during ramp years so users can
// see what would have been contributed vs. what actually was. A callout above the
// chart shows the cumulative shortfall over the adjustment period.
//
// Step 13 (2026-08-05) — top-right of the header now hosts a "More" chooser
// that opens the two on-demand analytical charts:
//   1. Funding Level Analysis (live)
//   2. [chart 2 — coming soon, awaiting spec]
// Custom X-axis tick — hoisted per react/no-unstable-nested-components.
// Translates the whole group -1 px x / +2 px y BEFORE the -90° rotation
// so the shift lands in screen space, not rotated-text space.
function ActivityXAxisTick({ x, y, payload }) {
  return (
    <text
      x={x}
      y={y}
      dy={10}
      textAnchor="middle"
      fill="#1A1A1A"
      fontSize={9}
      fontFamily="IBM Plex Mono"
      fontWeight={700}
    >
      {payload.value}
    </text>
  );
}

// Hollow ring with a white border for the per-member Special Assessment
// scatter. Anchor = TOP of the coral SA column (which is stacked BELOW
// the light-blue Expenditures column in the negative half, so its top
// edge sits at data y = `-|expenditures|`). The shape offsets 12 px
// DOWN in screen space so the ring lands INSIDE the coral bar just
// below its top edge. Fill removed so the coral shows through the ring
// (Dave 2026-02-15 revised — earlier saDotY = 0 was landing inside the
// light-blue Expenditures column, not the coral). Label font kept at
// 8 px for a light footprint.
// SPECIAL ASSESSMENTS per-member marker.
// 2026-02-15 (Dave, v6) — solid BLACK dot sitting JUST BELOW the coral
// SA column (was solid coral, inside the top of the bar). Anchor now
// points to the BOTTOM edge of the SA bar (`expNeg - sa` in data
// space), and the shape offsets the dot +8 px DOWN in screen space to
// clear the fill.
// Small but non-zero segments render at least MIN_SEG_PX tall so they're always noticeable.
const MIN_SEG_PX = 4;
function MinVisibleBar({ x, y, width, height, fill, payload, dataKey, negative }) {
  if (!payload || !payload[dataKey]) return null;
  const top = height >= 0 ? y : y + height;
  const h = Math.abs(height);
  if (h >= MIN_SEG_PX) return <rect x={x} y={top} width={width} height={h} fill={fill} />;
  const yy = negative ? top : top + h - MIN_SEG_PX;
  // Same solid color as a true-size piece so every year reads consistently.
  return <rect x={x} y={yy} width={width} height={MIN_SEG_PX} fill={fill} data-testid="min-visible-marker" />;
}

// Axis-break rule: tallest bar ≥ ratio × next tallest → break it (max 2); axis caps at headroom × next.
// Ratios come from Platform Settings (chart_break_*_pct).
const SHOW_SCALE_TOGGLE = false;
// Reserve Balance checkbox hidden per Dave; the series always shows. Flip to bring it back.
const SHOW_RESERVE_TOGGLE = false;
// 2nd bar breaks on its own when ≥ SECOND_RATIO × 3rd (even if close to the tallest).
function findBreaks(rows, BREAK_RATIO, CAP_HEADROOM, SECOND_RATIO, SECOND_ON = true, FIRST_ON = true) {
  // Tallest-bar switch Off = no breaks at all (a 2nd-bar break needs the tallest broken too).
  if (!FIRST_ON) return null;
  // Only expense bars (below the line) are eligible to break; contributions always draw to scale.
  const mags = rows.map((r, i) => ({ i, dir: -1, mag: -(r.expenditures + r.special) }))
    .sort((a, b) => b.mag - a.mag);
  if (mags.length < 3 || !(mags[1].mag > 0)) return null;
  const one = mags[0].mag >= BREAK_RATIO * mags[1].mag;
  const two = SECOND_ON && mags.length >= 4 && mags[2].mag > 0 && mags[1].mag >= SECOND_RATIO * mags[2].mag;
  if (!one && !two) return null;
  const broken = two ? mags.slice(0, 2) : mags.slice(0, 1);
  const cap = CAP_HEADROOM * mags[broken.length].mag;
  // A break only shortens bars: skip when the trigger is at/below the axis headroom.
  if (broken.some((b) => b.mag <= cap)) return null;
  return { broken, cap };
}

// Zig-zag break across a clipped bar plus its true-value label.
function BarBreakMark({ cx, cy, payload, barWidth }) {
  if (cx == null || cy == null || !payload?.breakInfo) return null;
  const { dir, total } = payload.breakInfo;
  const w = (barWidth || 22) + 4;
  const x0 = cx - w / 2;
  const amp = 3;
  const zig = (dy) => `M ${x0} ${cy + dy} L ${x0 + w / 3} ${cy + dy - amp} L ${x0 + (2 * w) / 3} ${cy + dy + amp} L ${x0 + w} ${cy + dy}`;
  const sign = dir < 0 ? "-" : "";
  const label = total >= 1e6 ? `${sign}$${(total / 1e6).toFixed(2)}M` : `${sign}$${Math.round(total / 1e3)}K`;
  const ly = cy + 9; // label nudged 9px below the break
  const lw = label.length * 5.5 + 8;
  const lx = x0 - 3 - lw / 2; // label sits immediately left of the break
  return (
    <g data-testid={`bar-break-${payload.year}`}>
      <path d={`${zig(-3)} L ${x0 + w} ${cy + 3} L ${x0 + (2 * w) / 3} ${cy + 3 + amp} L ${x0 + w / 3} ${cy + 3 - amp} L ${x0} ${cy + 3} Z`} fill="#FFFFFF" />
      <path d={zig(-3)} fill="none" stroke="#1C4E80" strokeWidth={1.25} />
      <path d={zig(3)} fill="none" stroke="#1C4E80" strokeWidth={1.25} />
      {/* True value sits in the stub just past the break. */}
      <rect x={lx - lw / 2} y={ly - 6} width={lw} height={12} rx={2} fill="#FFFFFF" stroke="#1C4E80" strokeWidth={0.75} />
      <text x={lx} y={ly + 3} textAnchor="middle" fontSize={9} fontWeight={700} fill="#1C4E80" fontFamily="IBM Plex Mono">{label}</text>
    </g>
  );
}



function SAPerMemberDot({ cx, cy, payload }) {
  if (
    cx == null ||
    cy == null ||
    !payload ||
    payload.saDotY == null ||
    payload.saPerMember == null
  ) {
    return null;
  }
  const dotY = cy + 8; // 8 px BELOW the bottom of the coral SA column
  return (
    <g>
      <circle
        cx={cx}
        cy={dotY}
        r={3}
        fill="#000000"
      />
      <text
        x={cx + 6}
        y={dotY}
        dy={3}
        textAnchor="start"
        fontSize={8}
        fontFamily="IBM Plex Mono"
        fontWeight={600}
        fill="#1C4E80"
      >
        {`$${payload.saPerMember.toLocaleString()}`}
      </text>
    </g>
  );
}

// Hollow ring with a white border for the Contribution "Per Member Per
// Month" (PMPM) scatter. Anchor = TOP of the NAVY Contribution column
// (data y = `contribution`, positive half). The shape offsets 12 px
// DOWN in screen space so the ring lands INSIDE the navy bar just
// below its top edge. Fill removed so the navy shows through the ring
// (Dave 2026-02-15 final — reverted to the navy Contribution bar
// because PMPM = contribution ÷ members ÷ 12 is derived from that
// column's value).
// CONTRIBUTIONS "Per Member Per Month" marker.
// 2026-02-15 (Dave, v6) — solid BLACK dot sitting JUST ABOVE the navy
// Contribution column (was solid navy, inside the top of the bar).
// Anchor stays at the TOP of the navy bar (data y = the `contribution`
// value); the shape now offsets the dot -8 px UP in screen space so it
// clears the navy fill and hovers above it.
// Funding Level % marker — fixed row just under the top grid line (margin.top = 34).
// Same cut-offs as the Contributions tab + report: High < 33.34%, Moderate < 66.67%, else Low (unrounded %).
const FUND_BANDS = [
  { max: 33.34, fill: "#F8C9BC", stroke: "#EA6A47" },
  { max: 66.67, fill: "#FBE7A6", stroke: "#D9A82E" },
  { max: Infinity, fill: "#C6E7CC", stroke: "#5FAE6E" },
];
function FundingPctDot({ cx, payload, onSelect, selected }) {
  if (cx == null || !payload || payload.fundingPct == null) return null;
  const pct = payload.fundingPct;
  const band = FUND_BANDS.find((b) => payload.fundingPctRaw < b.max);
  // Risk tier by position: green top sits 3px below the top grid line (y=14, chart margin.top);
  // yellow 19px lower than green; red 19px lower than yellow.
  const greenCy = 14 + 3 + 9.5;
  const cy = band === FUND_BANDS[2] ? greenCy : band === FUND_BANDS[1] ? greenCy + 19 : greenCy + 38;
  return (
    <g
      data-testid={`funding-pct-marker-${payload.year}`}
      style={{ cursor: "pointer" }}
      onClick={(e) => { e.stopPropagation(); onSelect && onSelect(payload, cx, cy); }}
    >
      <circle cx={cx} cy={cy} r={9.5} fill={band.fill} stroke={band.stroke} strokeWidth={selected ? 1.75 : 0.75} />
      <text x={cx} y={cy} dy={3} textAnchor="middle" fontFamily="IBM Plex Mono" fontWeight={600} fill="#1A1A1A">
        <tspan fontSize={7.5}>{pct}</tspan>
        <tspan fontSize={6.5}>%</tspan>
      </text>
    </g>
  );
}

function FundingDetailPopover({ detail, flipLeft, onClose }) {
  const band = FUND_BANDS.find((b) => detail.fundingPctRaw < b.max);
  return (
    <div
      className="absolute bg-white rounded-[4px] shadow-md text-[11px] text-ink"
      style={{
        top: detail.y + 14,
        left: flipLeft ? detail.x - 232 : detail.x - 12,
        width: 220,
        zIndex: 6,
        border: "0.5px solid #1C4E80",
        borderTop: `3px solid ${band.stroke}`,
        fontFamily: "Source Sans 3",
      }}
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      data-testid="funding-detail-popover"
    >
      <div className="flex items-center justify-between px-3 pt-2 pb-1">
        <span className="text-[10px] tracking-[0.14em] uppercase font-bold text-navy">{detail.year} Funding Level</span>
        <span className="font-mono tabular-nums font-semibold" data-testid="funding-detail-pct">{detail.fundingPct}%</span>
      </div>
      <div className="px-3 pb-2 flex flex-col gap-0.5">
        <div className="flex justify-between">
          <span className="text-slate">Reserve Balance</span>
          <span className="font-mono tabular-nums" data-testid="funding-detail-reserve">{fmtCurrency(detail.reserveBalance)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate">Fully Funded Balance</span>
          <span className="font-mono tabular-nums" data-testid="funding-detail-ffb">{fmtCurrency(detail.fullyFunded)}</span>
        </div>
      </div>
    </div>
  );
}

function ContributionPMPMDot({ cx, cy, payload }) {
  if (
    cx == null ||
    cy == null ||
    !payload ||
    payload.contribDotY == null ||
    payload.contribPMPM == null
  ) {
    return null;
  }
  const dotY = cy - 10; // 10 px ABOVE the top of the navy Contribution column
  return (
    <g>
      <circle
        cx={cx}
        cy={dotY}
        r={2.25}
        fill="#000000"
      />
      <text
        x={cx + 5}
        y={dotY}
        dy={3}
        textAnchor="start"
        fontSize={8}
        fontFamily="IBM Plex Mono"
        fontWeight={600}
        fill="#1C4E80"
      >
        {`$${payload.contribPMPM.toLocaleString()}`}
      </text>
    </g>
  );
}

export default function ActivityChart({ calc, variables, horizon, association }) {
  const [activeChart, setActiveChart] = useState(null);

  // 2026-02-26 (Dave) — Cross-component opener for the Funding Level
  // Analysis modal. FundingLevelGauge's "nn%" percentage now dispatches
  // `ureserve:open-funding-level-analysis` when clicked (see the hover-
  // hint button around the fl-funding-level cell in FundingLevelGauge.jsx).
  // We listen here so the chart's owning component still controls its
  // own modal state — no prop drilling through DashboardScreen required.
  useEffect(() => {
    const handler = () => setActiveChart("funding-level-analysis");
    window.addEventListener("ureserve:open-funding-level-analysis", handler);
    return () => window.removeEventListener("ureserve:open-funding-level-analysis", handler);
  }, []);

  // "Per Member" demo reveal — hidden by default. Clicking the item in the
  // More menu flips this on for REVEAL_MS then auto-hides. A second click
  // during the reveal window dismisses immediately (Dave 2026-02-15).
  //
  // 2026-02-27 (Dave) — Per-Member reveal now reads its OWN
  // Admin → Settings → Chart Reveals knob (`per_member_reveal_seconds`,
  // default 15 s). The previous wire was pointing at
  // `hover_visible_seconds` — the wrong knob — so retuning the
  // Per Member duration in Admin didn't change this reveal's
  // duration. The other two hover reveals (Waterfall detail,
  // chart-modal hover) still use `hover_visible_seconds`.
  const { settings } = useSettings();
  const REVEAL_MS = Math.max(1, Number(settings.per_member_reveal_seconds || 15)) * 1000;
  // One reveal at a time: "pmpm" (Per Member markers) or "funding" (Funding Level % markers).
  const [revealMode, setRevealMode] = useState(null);
  const [fundHintOff, setFundHintOff] = useState(false);
  const revealed = revealMode === "pmpm";
  const fundingRevealed = revealMode === "funding";
  const [showReserve, setShowReserve] = useState(true);
  const [toScale, setToScale] = useState(false);
  const barWidthRef = useRef(22);
  const [fundDetail, setFundDetail] = useState(null); // { year, x, y, ... } of the clicked marker
  useEffect(() => { if (!fundingRevealed) setFundDetail(null); }, [fundingRevealed]);
  const revealTimerRef = useRef(null);
  const plotWidthRef = useRef(0);
  const titleRef = useRef(null);
  const horizonRef = useRef(null);
  const plotRef = useRef(null);
  // 2026-02-23 (Dave) — Tooltip position tracker.
  //
  // Recharts' default tooltip position sits to the RIGHT of the cursor
  // with a small offset. For columns near the right edge of the chart
  // that overflows the plot area, and recharts then clamps back to
  // x=0 → "tooltip on the far left" symptom on right-half columns.
  //
  // Fix: track the cursor via ComposedChart's onMouseMove and set
  // `position={tooltipPos}`. Past 55% of the columns → flip the
  // tooltip to the LEFT of the cursor so it always fits.
  //
  // Batching note: setState inside recharts' onMouseMove is inside the
  // same React 18 event batch as recharts' own internal setState, so
  // the tooltip renders ONCE with our position — no first-frame hop.
  const [tooltipPos, setTooltipPos] = useState(undefined);
  const dataLenRef = useRef(0);

  // 2026-02-27 (evening, Dave) — Click-to-reveal state. Hover shows
  // the gray column tint (via Recharts' `cursor` prop below), a
  // click on any column commits that column's label into
  // `revealedLabel`; the detail tooltip then renders only for that
  // column. Clicking the same column again dismisses. Clicking a
  // different column swaps in the new column.
  const [revealedLabel, setRevealedLabel] = useState(null);
  const handleChartClick = (state) => {
    if (!state || state.activeLabel === undefined || state.activeLabel === null) return;
    setRevealedLabel((prev) => (prev === state.activeLabel ? null : state.activeLabel));
  };

  // Tooltip width estimate — the container has `minWidth: 220` and the
  // number column adds a few px. 250 gives us enough headroom to keep
  // the tooltip inside the viewport when flipped left.
  const TOOLTIP_EST_WIDTH = 250;
  // Vertical offset — position the tooltip ABOVE the cursor so it
  // never gets clipped by the bottom of the viewport (the chart lives
  // in the lower half of the dashboard, so "always above" is safe).
  const TOOLTIP_ABOVE = 130;

  const handleChartMouseMove = (state) => {
    if (!state || !state.isTooltipActive || !state.activeCoordinate) return;
    const { x: cursorX, y: cursorY } = state.activeCoordinate;
    const idx = state.activeTooltipIndex ?? state.activeIndex ?? 0;
    const total = dataLenRef.current || 1;
    const flipLeft = idx / total > 0.55;
    const x = flipLeft ? cursorX - TOOLTIP_EST_WIDTH - 12 : cursorX + 16;
    const y = Math.max(6, cursorY - TOOLTIP_ABOVE);
    setTooltipPos({ x, y });
  };

  const handleChartMouseLeave = () => {
    setTooltipPos(undefined);
  };

  useEffect(
    () => () => {
      if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    },
    [],
  );

  const dismissPerMemberReveal = () => {
    if (!revealMode) return;
    setRevealMode(null);
    if (revealTimerRef.current) {
      clearTimeout(revealTimerRef.current);
      revealTimerRef.current = null;
    }
  };

  const toggleReveal = (mode) => {
    if (revealMode === mode) {
      dismissPerMemberReveal();
      return;
    }
    setRevealMode(mode);
    if (revealTimerRef.current) clearTimeout(revealTimerRef.current);
    revealTimerRef.current = setTimeout(() => {
      setRevealMode(null);
      revealTimerRef.current = null;
    }, REVEAL_MS);
  };
  const togglePerMemberReveal = () => toggleReveal("pmpm");
  // Lets the Results at a Glance PMPM arrow turn while the reveal is showing.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("ureserve:pmpm-reveal-state", { detail: revealMode === "pmpm" }));
  }, [revealMode]);
  // Results at a Glance "Per Member" row fires this to toggle the same reveal.
  const pmpmToggleRef = useRef(togglePerMemberReveal);
  pmpmToggleRef.current = togglePerMemberReveal;
  useEffect(() => {
    const h = () => pmpmToggleRef.current();
    window.addEventListener("ureserve:toggle-per-member-reveal", h);
    return () => window.removeEventListener("ureserve:toggle-per-member-reveal", h);
  }, []);

  if (!calc || !variables) return null;


  const ffbByYear = calc.ffb_peak_by_year || []; // server-computed
  const dYears = calc.display?.years || [];
  const data = calc.projection.slice(0, horizon).map((r, idx) => {
    const adjustment = r.adjustment || 0; // server-computed (display_series.py)
    const excess = Math.round(r.adjustment_excess || 0); // server-computed
    const sa = r.special_assessment || 0;
    const contribution = Math.round(r.contribution);
    const exp = Math.round(r.expenditures);
    const saRounded = Math.round(sa);
    // 2026-02-23 (Dave) — Split expenditures for stacking: light-blue
    // "Reserves" bar shows only the portion of expenditures NOT funded
    // by a Special Assessment; coral SA bar shows the SA portion. Sum
    // of the two = full Expenditures, so the total column depth still
    // reflects true annual expenditures (no double-count when SA is
    // present). Clamp reservesFunded to ≥ 0 so an SA that exceeds
    // expenditures (rare — SA used to rebuild reserves) doesn't push
    // the blue bar into positive territory.
    const reservesFunded = Math.max(0, exp - saRounded);
    return {
      year: r.year,
      // Positive adjustment: navy = normal amount, green = extra on top.
      contribution: contribution - excess,
      adjustmentExcess: excess,
      adjustment: Math.round(adjustment),
      // Charted (blue) — reserves-funded portion of expenditures only.
      expenditures: -reservesFunded,
      // Charted (coral) — SA portion. Stacked below the blue bar.
      special: -saRounded,
      // Tooltip-only positive values.
      expTotal: exp,
      reservesFunded,
      saAmount: saRounded,
      // Per-member special assessment (annual). `null` in years without
      // an SA so recharts skips those rows.
      saPerMember: sa > 0 ? Math.round(dYears[idx]?.sa_per_member || 0) : null,
      // Anchor for the SA-per-member marker = bottom edge of the coral
      // SA bar in the stack. With the new split-stack, the coral bar
      // sits at [-reservesFunded, -(reservesFunded + sa)] in data space
      // (i.e., its bottom = -expenditures when sa ≤ exp, else -sa).
      // Dot offsets +8 px DOWN in screen space to clear the coral fill.
      saDotY: sa > 0 ? -reservesFunded - saRounded : null,
      // Contribution "Per Member Per Month" (PMPM). Derived from the
      // navy Contribution bar (annual contribution ÷ unit_count ÷ 12).
      // Anchor = TOP of the navy Contribution column (data y = the
      // `contribution` value itself, positive half). Dave 2026-02-15
      // (v6) — marker sits JUST ABOVE the Contribution bar, so the
      // shape offsets the dot -8 px UP in screen space to clear the
      // navy fill. `null` in years with zero contribution so the dot
      // is skipped.
      contribDotY: contribution > 0 ? contribution : null,
      contribPMPM:
        contribution > 0 ? Math.round(dYears[idx]?.contribution_pmpm || 0) : null,
      // Same Funding Level % as the Funding Level Analysis chart (server-computed).
      fundingPct: dYears[idx]?.funding_pct != null ? Math.round(dYears[idx].funding_pct) : null,
      fundingPctRaw: dYears[idx]?.funding_pct ?? null,
      reserveBalance: Math.round(r.ending), // also plotted on the right axis
      fullyFunded: Math.round(ffbByYear[idx] || 0),
      fundDotY: 0,
    };
  });

  const BREAK_RATIO = Number(settings.chart_break_trigger_pct || 115) / 100;
  const CAP_HEADROOM = Number(settings.chart_break_headroom_pct || 105) / 100;
  const BREAK_AT = Number(settings.chart_break_position_pct || 85) / 100;
  const SECOND_RATIO = Number(settings.chart_break_second_trigger_pct || 130) / 100;
  const SECOND_ON = Number(settings.chart_break_second_enabled ?? 1) === 1;
  const FIRST_ON = Number(settings.chart_break_first_enabled ?? 1) === 1;
  const breaks = toScale ? null : findBreaks(data, BREAK_RATIO, CAP_HEADROOM, SECOND_RATIO, SECOND_ON, FIRST_ON);
  const canBreak = !!(toScale ? findBreaks(data, BREAK_RATIO, CAP_HEADROOM, SECOND_RATIO, SECOND_ON, FIRST_ON) : breaks);
  if (breaks) {
    for (const b of breaks.broken) {
      const r = data[b.i];
      const f = breaks.cap / b.mag;
      r.raw = { contribution: r.contribution, adjustment: r.adjustment, adjustmentExcess: r.adjustmentExcess };
      const keys = b.dir > 0 ? ["contribution", "adjustmentExcess", "adjustment"] : ["expenditures", "special"];
      for (const k of keys) r[k] = r[k] * f;
      if (b.dir > 0 && r.contribDotY != null) r.contribDotY = r.contribution;
      if (b.dir < 0 && r.saDotY != null) r.saDotY = r.expenditures + r.special;
      r.breakY = b.dir * breaks.cap * BREAK_AT;
      r.breakInfo = { dir: b.dir, total: b.mag };
    }
  }

  const anySA = data.some((r) => r.saDotY != null);
  const anyShortfall = data.some((r) => r.adjustment > 0);
  // Shared-zero axes: left gets explicit nice ticks; right (Reserve Balance) starts at the left's $0 line.
  const niceStep = (span, n) => {
    const raw = Math.max(1, span) / n;
    const mag = 10 ** Math.floor(Math.log10(raw));
    return [1, 2, 2.5, 5, 10].map((m) => m * mag).find((v) => v >= raw);
  };
  const posMax = Math.max(0, ...data.map((r) => r.contribution + (r.adjustmentExcess || 0) + r.adjustment));
  const negMin = Math.min(0, ...data.map((r) => r.expenditures + r.special));
  // Axis ends hug the 30-yr extremes: ~2% headroom, rounded to $25K (or finer);
  // gridlines stay on the coarser nice step, and each end gets its own label.
  const HEADROOM = 0.02;
  const roundOut = (v, step) => (v >= 0 ? Math.ceil(v / step) : Math.floor(v / step)) * step;
  const withEnds = (inner, lo, hi, step) => {
    const keep = inner.filter((v) => v - lo > step * 0.45 && hi - v > step * 0.45);
    return [lo, ...keep, hi];
  };
  const lSpan = posMax - negMin;
  const lStep = niceStep(lSpan, 6);
  const lFine = Math.min(25000, niceStep(lSpan, 18)); // ends round to $25K (finer for tiny plans)
  const lMax = Math.max(lFine, roundOut(posMax + lSpan * HEADROOM, lFine));
  // Extra bottom room keeps the deepest bar clear of the bottom legend row.
  const BOTTOM_ROOM = 0.12;
  const lMin = roundOut(negMin - lSpan * BOTTOM_ROOM, lFine);
  const leftGrid = [];
  for (let v = Math.ceil(lMin / lStep) * lStep; v <= lMax + 1e-6; v += lStep) leftGrid.push(Math.round(v));
  const leftTicks = withEnds(leftGrid, lMin, lMax, lStep);
  const maxEnding = Math.max(0, ...data.map((r) => r.reserveBalance));
  const rFine = Math.min(25000, niceStep(maxEnding, 18));
  const rMax = Math.max(rFine, roundOut(maxEnding * (1 + HEADROOM), rFine));
  const rMin = (lMin / lMax) * rMax; // keeps $0 aligned with the left axis
  const rStep = niceStep(rMax, 4);
  const rightGrid = [];
  for (let v = 0; v <= rMax + 1e-6; v += rStep) rightGrid.push(Math.round(v));
  const rightTicks = withEnds(rightGrid, 0, rMax, rStep);
  // Size each Y axis to its widest label (10px mono ≈ 6.1px/char + tick), pinned to ≥7 chars so horizon changes don't shift it.
  const labelW = (ticks) => Math.ceil(Math.max(7, ...ticks.map((v) => fmtCurrencyK(v).length)) * 6.1) + 12;
  const leftAxisW = labelW(leftTicks);
  const rightAxisW = labelW(rightTicks);
  const anyExcess = data.some((r) => r.adjustmentExcess > 0);
  const adjPeriod = variables.adjustment_period || 0;
  const showCumAdj = (variables.adjustment_pct || 0) !== 0 && adjPeriod > 0 && Math.round(calc.cumulative_adjustment_signed || 0) !== 0;
  const cumAdj = calc.cumulative_adjustment_signed || 0; // server-computed
  const anyPMPM = data.some((r) => r.contribDotY != null);

  // Track data length so the mouseMove flip-left threshold has an
  // up-to-date denominator without recreating handlers each render.
  dataLenRef.current = data.length;

  // Bar width — auto-adjust with a narrower starting width using barCategoryGap.
  // Recharts default gap is "10%"; higher values → thinner bars.
  // Also cap absolute width with maxBarSize so 5-year view doesn't get fat.
  const barCategoryGap = horizon <= 10 ? "55%" : "35%";
  const maxBarSize = horizon <= 10 ? 32 : 22;

  return (
    <div
      className="panel-compact attention-border"
      data-testid="activity-chart"
      style={{
        /* 2026-02-15 (Dave · Plan A) — flex-column swapped for an explicit
           grid with `minmax(0, 1fr)` on the plot row. Prevents the plot
           cell's intrinsic min-content height (which Recharts computes as
           roughly `Y-axis + X-axis + a couple of bars`) from pushing past
           the panel's `overflow: hidden` clip line and eating the X-axis
           year-label descenders on shorter viewports.
           `minmax(0, 1fr)` is the durable idiom: the plot cell is *capped*
           at whatever height remains after the header + subheader rows,
           and Recharts' <ResponsiveContainer> then sizes to that cell.
           Recharts internally reserves X-axis + Y-axis heights before
           drawing bars, so the year labels are mathematically guaranteed
           to render inside the panel from now on. */
        display: "grid",
        gridTemplateRows: "auto minmax(0, 1fr)",
      }}
    >
      <div
        className="dash-panel-header"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          columnGap: "1rem",
          height: 20,
          marginBottom: 3,
          paddingBottom: 1, // coral rule sits 3px higher; plot position unchanged
        }}
      >
        <div ref={titleRef} className="text-[10.5px] tracking-[0.14em] uppercase font-bold text-navy justify-self-start">
          Reserve Activity by Year
        </div>
        <div className="justify-self-center whitespace-nowrap text-[12px] font-bold text-navy relative" style={{ fontFamily: "Source Sans 3", top: -2 }} data-testid="activity-cumulative-adjustment">
          {showCumAdj && (
            <>
              Cumulative Adjustment:
              <span
                className="font-mono tabular-nums"
                style={{ marginLeft: "0.6ch", color: cumAdj < 0 ? "#D62828" : ADJ_GREEN }}
                data-testid="activity-cumulative-adjustment-value"
              >
                {cumAdj < 0 ? `-${fmtCurrency(-cumAdj)}` : fmtCurrency(cumAdj)}
              </span>
              {`\u00A0 Over ${adjPeriod} ${adjPeriod === 1 ? "Year" : "Years"}`}
            </>
          )}
        </div>
        <div className="justify-self-end flex items-center gap-3 whitespace-nowrap" data-testid="activity-header-funding">
          <div
            ref={horizonRef}
            className="text-[12px] tracking-[0.14em] uppercase font-bold text-navy"
            style={{ transform: "translateY(-2px)" }}
            data-testid="activity-horizon-label"
          >
            {horizon} Year Horizon
          </div>
        </div>
      </div>

      {/* Slice E6d — old centered legend row removed; legend now sits in
          the row above alongside the More button per Dave's request. */}

      <div
        style={{ width: "100%", minHeight: 0, position: "relative", cursor: revealMode ? "pointer" : "default" }}
        ref={plotRef}
        data-testid="activity-chart-plot"
        className="wf-nofocus"
        onClick={dismissPerMemberReveal}
        title={revealMode ? "Click to dismiss markers" : undefined}
      >
        {/* Bottom strip: pills (left) · legend (centered) · adjustment / risk key (right). */}
        <div
          className="absolute grid items-center gap-4 text-[12px]"
          style={{ left: leftAxisW + 21, right: rightAxisW + 8, bottom: 38, height: 20, zIndex: 5, gridTemplateColumns: "max-content minmax(max-content, 1fr)", color: "#1A1A1A", fontFamily: "Source Sans 3", whiteSpace: "nowrap" }}
          data-testid="activity-bottom-row"
        >
          <div className="flex items-center gap-7 pointer-events-none justify-self-start" style={{ background: "rgba(255,255,255,0.85)", padding: "0 4px" }} data-testid="activity-legend">
              {[
                ...(anyShortfall ? [{ color: "#D62828", label: "Contributions Not Made" }] : []),
                ...(anyExcess ? [{ color: ADJ_GREEN, label: "Additional Contributions Made" }] : []),
                { color: "#1C4E80", label: "Contributions" },
                { color: "#A9D2EA", label: "From Reserves" },
                { color: "#EA6A47", label: "Special Assessments" },
                { color: RESERVE_GREEN, label: "Reserve Balance", line: true },
              ].map((it) => (
                <span key={it.label} className="inline-flex items-center gap-1.5">
                  {it.line ? (
                    <svg width="16" height="10" aria-hidden="true">
                      <line x1="0" y1="5" x2="16" y2="5" stroke={it.color} strokeWidth="1" />
                      <circle cx="8" cy="5" r="2.5" fill={it.color} />
                    </svg>
                  ) : (
                    <span
                      className="inline-block"
                      style={{
                        width: 10,
                        height: 10,
                        background: it.color,
                      }}
                    />
                  )}
                  {it.label}
                  {SHOW_RESERVE_TOGGLE && it.line && (
                    <input
                      type="checkbox"
                      checked={showReserve}
                      onChange={(e) => setShowReserve(e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      className="pointer-events-auto cursor-pointer"
                      style={{ accentColor: RESERVE_GREEN, width: 12, height: 12 }}
                      aria-label="Show Reserve Balance"
                      data-testid="reserve-balance-toggle"
                    />
                  )}
                </span>
              ))}
          </div>
          <div className="flex items-center gap-4 justify-self-end pointer-events-none">
          </div>
        </div>
        <div
          className="absolute"
          style={{ left: leftAxisW + 18, bottom: "calc(24px + (100% - 38px) / 3 - 12px)", transform: "translateY(50%)", zIndex: 6 }}
          onClick={(e) => e.stopPropagation()}
          data-testid="activity-left-controls"
        >
            <button
              type="button"
              onClick={() => { setFundHintOff(true); toggleReveal("funding"); }}
              onMouseLeave={() => setFundHintOff(false)}
              className="group relative inline-flex items-center"
              style={{ transform: "none" }}
              aria-pressed={fundingRevealed}
              aria-label={`Average Funding Level over ${horizon} years`}
              data-testid="funding-pct-toggle-btn"
            >
              <span
                className={"pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-1.5 whitespace-nowrap rounded-[3px] border border-[#EA6A47] bg-[#1C4E80] text-white text-[11px] font-semibold px-2 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150" + (fundHintOff ? " !opacity-0" : "")}
                data-testid="funding-pct-tooltip"
              >
                Average Funding Level
              </span>
              {(() => {
                // Server-computed: per-year clipped 0..100, mean over horizon (matches Contributions tab).
                if (!data.length) return null;
                const avgRaw = calc.display?.horizons?.[String(data.length)]?.avg_funding_pct ?? 0;
                const avg = Math.round(avgRaw);
                const band = FUND_BANDS.find((b) => avgRaw < b.max);
                return (
                  <>
                  <svg width="36" height="36" aria-hidden="true" data-testid="funding-pct-horizon-marker">
                    <circle cx="18" cy="18" r="16" fill={band.fill} stroke="#1C4E80" strokeWidth={fundingRevealed ? 2 : 1.25} />
                    <text x="18" y="18" dy={5} textAnchor="middle" fontFamily="IBM Plex Mono" fontWeight={700} fill="#1A1A1A">
                      <tspan fontSize={13.3}>{avg}</tspan>
                      <tspan fontSize={11.3}>%</tspan>
                    </text>
                  </svg>
                  <ChevronLeft size={16} className={"-ml-1 text-slate group-hover:text-navy transition-transform duration-200 " + (fundingRevealed ? "-rotate-90" : "")} aria-hidden="true" />
                  </>
                );
              })()}
            </button>
            {fundingRevealed && (
              <div className="absolute left-0 top-full mt-1 flex items-center gap-3 whitespace-nowrap text-[12px] pointer-events-none" style={{ background: "rgba(255,255,255,0.85)", padding: "0 4px", color: "#1A1A1A", fontFamily: "Source Sans 3" }} data-testid="funding-band-legend">
                {[["High Risk", 0, "0–33%"], ["Moderate Risk", 1, "34–66%"], ["Low Risk", 2, "67%+"]].map(([label, i, range]) => (
              <span key={label} className="inline-flex items-center gap-1" title={range} data-testid={`funding-band-legend-${i}`}>
                <svg width="10" height="10"><circle cx="5" cy="5" r="4.25" fill={FUND_BANDS[i].fill} stroke={FUND_BANDS[i].stroke} strokeWidth="0.75" /></svg>
                {label}
              </span>
            ))}
              </div>
            )}
                  </div>
        {/* Scale toggle hidden per Dave; flip SHOW_SCALE_TOGGLE to bring it back. */}
        {SHOW_SCALE_TOGGLE && canBreak && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setToScale((v) => !v); }}
            className="absolute inline-flex items-center justify-center bg-white hover:bg-navy-wash text-ink transition-colors rounded-[2px] px-1.5 py-[2px] text-[9.5px] font-medium leading-none"
            style={{ left: leftAxisW + 8, top: 19, zIndex: 5, border: "0.5px solid #1C4E80", fontFamily: "Source Sans 3" }}
            title={toScale ? "Shorten the tallest bar with a break so other years are easier to read" : "Draw every bar at its true height"}
            data-testid="activity-scale-toggle"
          >
            {toScale ? "Break Scale" : "At Scale"}
          </button>
        )}
        {fundingRevealed && fundDetail && (
          <FundingDetailPopover
            detail={fundDetail}
            flipLeft={fundDetail.x > (plotWidthRef.current || 0) * 0.7}
            onClose={() => setFundDetail(null)}
          />
        )}
        <ResponsiveContainer onResize={(w) => { plotWidthRef.current = w; }}>
          <ComposedChart
            data={data}
            margin={{ top: 14, right: 0, bottom: 6, left: 0 }}
            stackOffset="sign"
            barCategoryGap={barCategoryGap}
            maxBarSize={maxBarSize}
            onMouseMove={handleChartMouseMove}
            onMouseLeave={handleChartMouseLeave}
            onClick={handleChartClick}
          >
            <CartesianGrid yAxisId="left" stroke="#E8ECF1" vertical={false} />
            <XAxis
              dataKey="year"
              tick={<ActivityXAxisTick />}
              axisLine={{ stroke: "#1A1A1A" }}
              tickLine={{ stroke: "#1A1A1A" }}
              interval={0}
              height={18}
            />
            <YAxis
              yAxisId="left"
              tick={{ fill: "#1A1A1A", fontSize: 10, fontFamily: "IBM Plex Mono" }}
              axisLine={{ stroke: "#1A1A1A" }}
              tickLine={{ stroke: "#1A1A1A" }}
              tickFormatter={fmtCurrencyK}
              width={leftAxisW}
              domain={[lMin, lMax]}
              ticks={leftTicks}
              allowDataOverflow
            />
            {/* Right axis always reserves its 58px so the plot doesn't shift when Reserve Balance toggles. */}
            <YAxis
              yAxisId="right"
              orientation="right"
              domain={showReserve ? [rMin, rMax] : [0, 1]}
              ticks={showReserve ? rightTicks : []}
              allowDataOverflow
              tick={showReserve ? { fill: "#1A1A1A", fontSize: 10, fontFamily: "IBM Plex Mono" } : false}
              axisLine={showReserve ? { stroke: "#1A1A1A" } : false}
              tickLine={showReserve ? { stroke: "#1A1A1A" } : false}
              tickFormatter={fmtCurrencyK}
              width={rightAxisW}
            />
            <ReferenceLine y={0} yAxisId="left" stroke="#5A6B75" />
            <Tooltip
              content={
                <DelayedTooltipContent
                  inner={<ActivityTooltip revealedLabel={revealedLabel} />}
                  forceLabel={revealedLabel}
                />
              }
              /* 2026-02-27 (evening, Dave) — Hover-only cursor tint
                 (light-gray column shading). Details reveal on
                 click only — see the `revealedLabel` gate inside
                 <ActivityTooltip> and the ComposedChart `onClick`
                 handler above. */
              cursor={{ fill: "rgba(148, 163, 184, 0.20)" }}
              position={tooltipPos}
              // Recharts 3 skips its translate when content mounts late (click-reveal), so pin it ourselves.
              wrapperStyle={{ pointerEvents: "none", zIndex: 10, ...(tooltipPos ? { transform: `translate(${tooltipPos.x}px, ${tooltipPos.y}px)` } : {}) }}
              trigger="hover"
              allowEscapeViewBox={{ x: true, y: true }}
              isAnimationActive={false}
            />
            <Bar yAxisId="left" dataKey="contribution" name="Contributions" fill="#1C4E80" stackId="a" shape={(props) => {
                // Capture bar width via closure; Recharts freezes shape props in production builds.
                if (props.width) barWidthRef.current = props.width;
                return <Rectangle {...props} />;
              }} />
            {/* Always mounted so Recharts keeps it stacked directly above Contributions. */}
            <Bar yAxisId="left" dataKey="adjustmentExcess" name="Additional Contributions Made" fill={ADJ_GREEN} stackId="a" shape={<MinVisibleBar dataKey="adjustmentExcess" />} />
            <Bar yAxisId="left" dataKey="adjustment" name="Contributions Not Made" fill="#D62828" stackId="a" />{/* true size (no forced minimum) per Dave, trial */}
            <Bar yAxisId="left" dataKey="expenditures" name="From Reserves" fill="#A9D2EA" stackId="a" />
            <Bar yAxisId="left" dataKey="special" name="Special Assessments" fill="#EA6A47" stackId="a" shape={<MinVisibleBar dataKey="special" negative />} />
            {breaks && (
              <Scatter yAxisId="left" dataKey="breakY" name="Axis break" isAnimationActive={false}
                shape={(props) => <BarBreakMark {...props} barWidth={barWidthRef.current} />} />
            )}
            {revealed && anySA && (
              <Scatter
                yAxisId="left"
                dataKey="saDotY"
                name="SA per Member"
                fill="#1C4E80"
                shape={<SAPerMemberDot />}
                isAnimationActive={false}
              />
            )}
            {showReserve && <Line
              yAxisId="right"
              type="linear"
              dataKey="reserveBalance"
              name="Reserve Balance"
              stroke={RESERVE_GREEN}
              strokeWidth={1.75}
              dot={{ r: 2.6, fill: RESERVE_GREEN, stroke: RESERVE_GREEN, strokeWidth: 0 }}
              activeDot={{ r: 3.6, fill: RESERVE_GREEN, stroke: "#fff", strokeWidth: 1 }}
              isAnimationActive={false}
            />}
            {fundingRevealed && (
              <Scatter
                yAxisId="left"
                dataKey="fundDotY"
                name="Funding Level %"
                shape={(props) => (
                  <FundingPctDot
                    {...props}
                    selected={fundDetail?.year === props.payload?.year}
                    onSelect={(p, x, y) =>
                      setFundDetail((prev) => (prev?.year === p.year ? null : { ...p, x, y }))
                    }
                  />
                )}
                isAnimationActive={false}
              />
            )}
            {revealed && anyPMPM && (
              <Scatter
                yAxisId="left"
                dataKey="contribDotY"
                name="Contribution PMPM"
                fill="#88BFDE"
                shape={<ContributionPMPMDot />}
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>


      {/* Step 7.6 (2026-02-15) · Per-Member reveal legend — REMOVED
          2026-02-15 (Dave). The bottom-of-chart legend was consistently
          being clipped by the panel's `overflow: hidden` and didn't add
          real value on top of the on-chart hollow-ring markers (which
          have their own $-labelled dots and are self-explanatory). The
          top-of-chart legend row (Contribution Adjustment · Contributions
          · Expenditures · Special Assessments) is still the
          canonical legend for the four bar series. */}

      {/* Step 13 modal — mounted here so it lives inside the ActivityChart
          panel's React tree but portals-out via `fixed` positioning. */}
      <FundingLevelAnalysisChart
        open={activeChart === "funding-level-analysis"}
        onClose={() => setActiveChart(null)}
        calc={calc}
        variables={variables}
        association={association}
        horizon={horizon}
      />
    </div>
  );
}
